import React from 'react';
import { Kpi, Card, Bars, Donut, Scatter, Meter, ICN } from './Charts';

// Reports / Productivity tab — throughput, capacity, team.

const LANG_COLOR = {
    DE: 'var(--accent)', FR: 'var(--indigo)', JA: 'var(--green)', ES: 'var(--yellow)',
    IT: '#b06ad9', PT: '#4a9ed9', ZH: '#d9794a', KO: '#5aa0a0',
};
const utilColor = (u) => (u >= 95 ? '#c8553a' : u >= 80 ? 'var(--accent)' : 'var(--green)');

const ProductivityTab = ({ D, range }) => {
    const P = D.PROD_KPI;
    const flow = D.FLOW[range];
    const util = parseInt(P.capacity, 10) || 0;
    const maxBubbleWords = Math.max(1, ...D.TRANSLATORS.map((t) => t[4]));

    const points = D.TRANSLATORS.map(([name, ini, , lang, words, qual, , , wpd]) => ({
        x: wpd, y: qual, r: 6 + (words / maxBubbleWords) * 9, color: LANG_COLOR[lang] || 'var(--accent)', tag: ini, label: name,
    })).filter((p) => p.x > 0 && p.y > 0);

    return (
        <>
            <div className="rpt-kpis">
                <Kpi label="Words / linguist / day" value={P.wpd} delta={P.wpdDelta} arrow="up" tone="good" color="var(--accent)" />
                <Kpi label="Capacity utilization" value={P.capacity} delta={P.capDelta} arrow="up" tone="good" color="var(--green)" />
                <Kpi label="Active linguists" value={P.active} delta={P.activeDelta} arrow="up" tone="good" color="var(--indigo)" />
                <Kpi label="Backlog" value={P.backlog} delta={P.backlogDelta} arrow="down" tone="good" color="var(--yellow)" />
            </div>

            <div className="rpt-row a">
                <Card title="Throughput by stage" sub="words per period"
                    legend={[{ label: 'Human', color: 'var(--accent)' }, { label: 'MT post-edit', color: 'var(--indigo)' }, { label: 'In review', color: 'var(--green)' }]}>
                    <div className="rpt-plot">
                        <Bars labels={flow.labels} unit="k" stacked series={[
                            { name: 'Human', color: 'var(--accent)', data: flow.human },
                            { name: 'MT post-edit', color: 'var(--indigo)', data: flow.mt },
                            { name: 'In review', color: 'var(--green)', data: flow.review },
                        ]} />
                    </div>
                </Card>
                <Card title="Utilization">
                    <div className="rpt-donut-wrap col">
                        <Donut segments={[{ value: util, color: 'var(--green)', label: 'Utilized' }]} center={`${util}%`} sub="of capacity" />
                        <p className="rpt-note">Healthy band is <b>70–85%</b>. Above 90% signals overload risk and rising turnaround.</p>
                    </div>
                </Card>
            </div>

            <div className="rpt-row b">
                <Card title="Workload balance" sub="assigned vs capacity · per week">
                    <div className="rpt-pad">
                        <div className="rpt-cap">
                            {D.CAPACITY.length === 0 ? (
                                <div className="rpt-empty">No assigned workload yet.</div>
                            ) : D.CAPACITY.map(([name, assigned, capacity]) => {
                                const u = capacity ? Math.round((assigned / capacity) * 100) : 0;
                                return (
                                    <div className="rpt-cap-row" key={name}>
                                        <span className="nm">{name}</span>
                                        <span className="track"><Meter value={assigned} max={capacity} color={utilColor(u)} height={8} /></span>
                                        <span className="val">{assigned}k<span className="cap">/ {capacity}k</span></span>
                                        <span className="u" style={{ color: utilColor(u) }}>{u}%</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Card>
                <Card title="Speed vs quality" sub="bubble = volume · color = language">
                    <div className="rpt-plot sm">
                        {points.length === 0 ? (
                            <div className="rpt-empty">Not enough graded work to plot.</div>
                        ) : (
                            <Scatter points={points} w={460} h={300}
                                xMin={2400} xMax={3900} yMin={94} yMax={99}
                                xTicks={[2400, 2800, 3200, 3600]} yTicks={[94, 95, 96, 97, 98, 99]}
                                xLabel="words / day" yLabel="LQA score" />
                        )}
                    </div>
                </Card>
            </div>

            <Card title="Team performance" sub={`${D.TRANSLATORS.length} linguists · ${D.RANGE_LABEL[range]}`} right={<span className="rpt-link">View people {ICN.arrow}</span>}>
                <div className="rpt-tbl">
                    <div className="rpt-tr head team"><span>Linguist</span><span className="num">Words</span><span className="num">Quality</span><span className="num">On-time</span><span className="num">Turn</span><span className="num">W/day</span><span className="grow2">Utilization</span></div>
                    {D.TRANSLATORS.length === 0 ? (
                        <div className="rpt-empty">No linguist activity yet.</div>
                    ) : D.TRANSLATORS.map(([name, ini, av, lang, words, qual, ontime, turn, wpd, u]) => (
                        <div className="rpt-tr team link" key={name}>
                            <span className="who"><span className={`av-s ${av}`}>{ini}</span><span className="who-stack"><b>{name}</b><span className="rpt-code mini">{lang}</span></span></span>
                            <span className="num b">{words}k</span>
                            <span className="num">{qual}</span>
                            <span className="num">{ontime}%</span>
                            <span className="num">{turn}d</span>
                            <span className="num">{wpd.toLocaleString()}</span>
                            <span className="grow2 util"><Meter value={u} max={100} color={utilColor(u)} /><i>{u}%</i></span>
                        </div>
                    ))}
                </div>
            </Card>
        </>
    );
};

export default ProductivityTab;
